import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { QButton, QCard, QCardContent, QTypography } from '@kw/quokka-ui';
import dayjs from 'dayjs';

interface OrganizationSettingCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  valueLabel: string;
  value: ReactNode;
  lastUpdated?: string | Date;
  onEdit?: () => void;
}

interface OrganizationSettingPanelProps {
  title: string;
  description: string;
  icon: ReactNode;
  titleAdornment?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  fullWidthContent?: ReactNode;
}

const formatLastUpdated = (lastUpdated?: string | Date) => {
  if (!lastUpdated) return null;

  const parsedDate = dayjs(lastUpdated);
  return parsedDate.isValid() ? parsedDate.format('MMM D, YYYY') : null;
};

export const OrganizationSettingCard = ({
  title,
  description,
  icon,
  valueLabel,
  value,
  lastUpdated,
  onEdit,
}: OrganizationSettingCardProps) => {
  const formattedLastUpdated = formatLastUpdated(lastUpdated);

  return (
    <OrganizationSettingPanel
      title={title}
      description={description}
      icon={icon}
      actions={
        onEdit ? (
          <QButton variant="outlined" onClick={onEdit}>
            Edit
          </QButton>
        ) : undefined
      }
    >
      <SettingSummary>
        <div>
          <SummaryLabel>{valueLabel}</SummaryLabel>
          <SummaryValue>{value}</SummaryValue>
        </div>
        {formattedLastUpdated && (
          <LastUpdated>
            <SummaryLabel>Last updated</SummaryLabel>
            <LastUpdatedValue>{formattedLastUpdated}</LastUpdatedValue>
          </LastUpdated>
        )}
      </SettingSummary>
    </OrganizationSettingPanel>
  );
};

export const OrganizationSettingPanel = ({
  title,
  description,
  icon,
  titleAdornment,
  actions,
  children,
  fullWidthContent,
}: OrganizationSettingPanelProps) => (
  <StyledCard variant="outlined">
    <StyledCardContent>
      <PanelHeader data-has-full-width-content={Boolean(fullWidthContent)}>
        <IconTile aria-hidden="true">{icon}</IconTile>
        <CardBody>
          <CardHeader>
            <div>
              <TitleRow>
                <CardTitle variant="h5Medium" component="h2">
                  {title}
                </CardTitle>
                {titleAdornment}
              </TitleRow>
              <CardDescription variant="bodyRegular">{description}</CardDescription>
            </div>
            {actions && <HeaderActions>{actions}</HeaderActions>}
          </CardHeader>
          {children}
        </CardBody>
      </PanelHeader>
      {fullWidthContent}
    </StyledCardContent>
  </StyledCard>
);

const StyledCard = styled(QCard)`
  width: 100%;
  padding: 0;
  overflow: hidden;
  background: #ffffff;
`;

const StyledCardContent = styled(QCardContent)`
  padding: 0;

  &:last-child {
    padding-bottom: 0;
  }
`;

const PanelHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 24px;

  &[data-has-full-width-content='true'] {
    padding-bottom: 0;
  }
`;

const IconTile = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 8px;
  background: #f3f5f5;

  svg {
    width: 21px;
    height: 21px;
  }
`;

const CardBody = styled('div')`
  flex: 1;
  min-width: 0;
`;

const CardHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const HeaderActions = styled('div')`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;

  @media (max-width: 760px) {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const CardTitle = styled(QTypography)`
  display: block;
  margin: 0;
  font-size: 17px;
  line-height: 24px;
  font-weight: 700;
`;

const CardDescription = styled(QTypography)`
  display: block;
  max-width: 700px;
  margin-top: 6px;
  color: #57666b;
  font-size: 14px;
  line-height: 21px;
`;

const SettingSummary = styled('div')`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const SummaryLabel = styled('div')`
  color: #57666b;
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SummaryValue = styled('div')`
  margin-top: 7px;
  font-size: 15px;
  line-height: 21px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const LastUpdated = styled('div')`
  color: #57666b;
  flex-shrink: 0;
  text-align: right;
`;

const LastUpdatedValue = styled('div')`
  margin-top: 7px;
  font-size: 13px;
  line-height: 18px;
`;
